/**
 * Network-related helper functions
 * Provides utilities for network detection and upload estimation
 */

export type ConnectionType = 'wifi' | 'cellular' | 'unknown'

/**
 * Determines if a file upload should show a cellular data warning
 * @param fileSize File size in bytes
 * @param connectionType Current connection type
 * @returns true if warning should be shown
 */
export function shouldWarnForCellularUpload(
  fileSize: number,
  connectionType: ConnectionType
): boolean {
  const THRESHOLD_BYTES = 3 * 1024 * 1024 // 3MB

  // Show warning if on cellular and file is larger than threshold
  return connectionType === 'cellular' && fileSize > THRESHOLD_BYTES
}

/**
 * Estimates upload time based on file size and connection speed
 * @param fileSize File size in bytes
 * @param downlink Download speed in Mbps (from Network Information API)
 * @returns Estimated time in seconds
 */
export function estimateUploadTime(fileSize: number, downlink: number): number {
  if (downlink <= 0) {
    return 0 // Cannot estimate without speed info
  }

  // Assume upload speed is approximately 50% of download speed (typical for most connections)
  const uploadSpeedMbps = downlink * 0.5

  // Convert file size to megabits
  const fileSizeMegabits = (fileSize * 8) / (1024 * 1024)

  // Calculate time in seconds
  const estimatedSeconds = fileSizeMegabits / uploadSpeedMbps

  return Math.ceil(estimatedSeconds)
}

/**
 * Formats upload time estimate into human-readable string
 * @param seconds Time in seconds
 * @returns Formatted string like "2 minutes" or "30 seconds"
 */
export function formatUploadTime(seconds: number): string {
  if (seconds < 1) {
    return 'less than a second'
  }

  if (seconds < 60) {
    return `${seconds} second${seconds !== 1 ? 's' : ''}`
  }

  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60

  if (remainingSeconds === 0) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`
  }

  if (minutes === 0) {
    return `${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}`
  }

  return `${minutes} minute${minutes !== 1 ? 's' : ''} ${remainingSeconds} second${
    remainingSeconds !== 1 ? 's' : ''
  }`
}

/**
 * Formats file size in bytes to human-readable string
 * @param bytes File size in bytes
 * @returns Formatted string like "2.5 MB" or "150 KB"
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

/**
 * Classifies connection quality based on effective type and downlink speed
 * @param effectiveType Effective connection type ('4g', '3g', '2g', 'slow-2g')
 * @param downlink Download speed in Mbps
 * @returns 'fast' | 'normal' | 'slow'
 */
export function getConnectionQuality(
  effectiveType: string,
  downlink: number
): 'fast' | 'normal' | 'slow' {
  // Fast: 4G with good speed or any connection > 10 Mbps
  if (effectiveType === '4g' && downlink >= 5) {
    return 'fast'
  }

  if (downlink >= 10) {
    return 'fast'
  }

  // Slow: 2G, slow-2G, or very slow connections
  if (effectiveType === '2g' || effectiveType === 'slow-2g' || downlink < 1) {
    return 'slow'
  }

  // Normal: Everything in between
  return 'normal'
}

/**
 * Determines if a connection is cellular based on effective type
 * @param effectiveType Effective connection type
 * @returns true if connection is likely cellular
 */
export function isCellularConnection(effectiveType: string): boolean {
  // 4g, 3g, 2g, slow-2g are typically cellular
  return ['4g', '3g', '2g', 'slow-2g'].includes(effectiveType.toLowerCase())
}

/**
 * Gets a user-friendly connection name
 * @param effectiveType Effective connection type
 * @param connectionType Connection type (wifi/cellular/unknown)
 * @returns Human-readable connection name
 */
export function getConnectionName(
  effectiveType: string,
  connectionType: ConnectionType
): string {
  if (connectionType === 'wifi') {
    return 'WiFi'
  }

  if (connectionType === 'cellular') {
    switch (effectiveType.toLowerCase()) {
      case '4g':
        return '4G'
      case '3g':
        return '3G'
      case '2g':
        return '2G'
      case 'slow-2g':
        return 'Slow Connection'
      default:
        return 'Cellular Data'
    }
  }

  return 'Unknown Connection'
}

/**
 * Checks if the browser supports the Network Information API
 * @returns true if Network Information API is available
 */
export function supportsNetworkInformation(): boolean {
  if (typeof navigator === 'undefined') {
    return false
  }

  return !!(
    (navigator as any).connection ||
    (navigator as any).mozConnection ||
    (navigator as any).webkitConnection
  )
}
